import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon, UsersIcon, VideoIcon, BrainIcon, SmilePlusIcon, HeartIcon, CompassIcon, WalletIcon, FileCheck2Icon } from 'lucide-react';
interface ServiceCard {
  name: string;
  icon: React.ReactNode;
  description: string;
}
interface Tab {
  id: string;
  label: string;
  cards: ServiceCard[];
}
const tabs: Tab[] = [{
  id: 'medical',
  label: 'Medical Care',
  cards: [{
    name: 'Wellness & Prevention',
    icon: <ShieldCheckIcon className="h-8 w-8" data-id="element-33" />,
    description: 'Annual physicals, immunizations, screenings, and preventive care programs for all ages.'
  }, {
    name: 'Adult & Child Care',
    icon: <UsersIcon className="h-8 w-8" data-id="element-34" />,
    description: 'Comprehensive primary care for the whole family — from pediatrics to adult medicine.'
  }, {
    name: 'Telemedicine',
    icon: <VideoIcon className="h-8 w-8" data-id="element-35" />,
    description: 'Virtual visits with your provider from the comfort of home. Convenient, secure, and easy.'
  }]
}, {
  id: 'specialty',
  label: 'Specialty Services',
  cards: [{
    name: 'Behavioral Health',
    icon: <BrainIcon className="h-8 w-8" data-id="element-36" />,
    description: 'Integrated mental health counseling and substance use support within your care team.'
  }, {
    name: 'Dental Care',
    icon: <SmilePlusIcon className="h-8 w-8" data-id="element-37" />,
    description: 'Comprehensive dental services including cleanings, fillings, and preventive oral health.'
  }, {
    name: "Women's Health",
    icon: <HeartIcon className="h-8 w-8" data-id="element-38" />,
    description: 'OB/GYN services, family planning, prenatal care, and reproductive health support.'
  }]
}, {
  id: 'support',
  label: 'Support & Insurance',
  cards: [{
    name: 'Patient Navigation',
    icon: <CompassIcon className="h-8 w-8" data-id="element-39" />,
    description: 'Dedicated navigators to help you coordinate care, referrals, and community resources.'
  }, {
    name: 'Sliding Fee Scale',
    icon: <WalletIcon className="h-8 w-8" data-id="element-40" />,
    description: 'Affordable care based on your income. No one is turned away for inability to pay.'
  }, {
    name: 'Insurance Assistance',
    icon: <FileCheck2Icon className="h-8 w-8" data-id="element-41" />,
    description: 'Help enrolling in Medicaid, Medicare, marketplace plans, and understanding your benefits.'
  }]
}];
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
      delay: i * 0.1
    }
  })
};
export function AreasOfConcentration() {
  const [activeTab, setActiveTab] = useState('medical');
  const activeContent = tabs.find(t => t.id === activeTab);
  return <section className="py-20 md:py-28 bg-white" aria-labelledby="services-heading" data-id="element-42">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-43">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14" data-id="element-44">
          <h2 id="services-heading" className="text-3xl md:text-4xl font-bold text-navy-500 mb-4" data-id="element-45">
            Comprehensive Care for You and Your Family
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-46">
            As a Patient-Centered Medical Home (PCMH), our patients receive
            preventative services and screenings at a higher rate.
          </p>
        </div>

        {/* Tab Bar */}
        <div className="relative mb-10 md:mb-14" data-id="element-47">
          <div className="flex overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 sm:justify-center gap-0 border-b border-[#E5E9F0]" role="tablist" aria-label="Service categories" data-id="element-48">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} role="tab" aria-selected={isActive} aria-controls={`panel-${tab.id}`} id={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)} className={`relative whitespace-nowrap px-5 md:px-8 py-4 text-sm md:text-base font-semibold transition-colors min-h-[48px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 focus-visible:ring-offset-2 rounded-t-md ${isActive ? 'text-navy-500' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-49">
                  {tab.label}
                  {isActive && <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-[4px] bg-navy-500 rounded-t-full" transition={{
                type: 'spring',
                stiffness: 400,
                damping: 30
              }} data-id="element-50" />}
                </button>;
          })}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait" data-id="element-51">
          {activeContent && <motion.div key={activeContent.id} role="tabpanel" id={`panel-${activeContent.id}`} aria-labelledby={`tab-${activeContent.id}`} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-52">
              {activeContent.cards.map((card, index) => <motion.a key={card.name} href={`#${card.name.toLowerCase().replace(/\s+/g, '-')}`} custom={index} variants={cardVariants} initial="hidden" animate="show" className="group flex flex-col items-center text-center bg-white border border-[#E5E9F0] rounded-[12px] p-8 md:p-10 transition-all duration-300 hover:bg-navy-500 hover:border-navy-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 focus-visible:ring-offset-2 min-h-[200px]" data-id="element-53">
                  <div className="text-navy-500 group-hover:text-white transition-colors duration-300 mb-5" data-id="element-54">
                    {card.icon}
                  </div>
                  <h3 className="text-lg font-bold text-navy-500 group-hover:text-white transition-colors duration-300 mb-3" data-id="element-55">
                    {card.name}
                  </h3>
                  <p className="text-sm text-gray-500 group-hover:text-white/80 transition-colors duration-300 leading-relaxed" data-id="element-56">
                    {card.description}
                  </p>
                </motion.a>)}
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}